import { useEffect, useState, type FC } from "react";
import {
  CircleMarker,
  MapContainer,
  TileLayer,
  Tooltip,
  useMap,
  useMapEvents,
} from "react-leaflet";
import { KIND_COLORS, primaryKind } from "../../lib/access";
import { cartoTiles } from "../../lib/carto";
import type { Fest } from "../../types";

interface FestMapProps {
  fests: Fest[];
  selectedId: string | null;
  onSelectFest: (fest: Fest) => void;
  onInspect: (lat: number, lng: number) => void;
  flyTo: { latitude: number; longitude: number; zoom?: number } | null;
  inspect: { latitude: number; longitude: number } | null;
}

function pinPath(fest: Fest, selected: boolean) {
  const tones = KIND_COLORS[primaryKind(fest)];
  if (selected) {
    return {
      color: "#FA5468",
      fillColor: "#D32A46",
      fillOpacity: 0.95,
      weight: 3,
    };
  }
  return {
    color: tones.color,
    fillColor: tones.fill,
    fillOpacity: 0.8,
    weight: 1.5,
  };
}

function SetupMap() {
  const map = useMap();

  useEffect(() => {
    const invalidate = () => map.invalidateSize();
    invalidate();
    const timer = window.setTimeout(invalidate, 100);
    window.addEventListener("resize", invalidate);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("resize", invalidate);
    };
  }, [map]);

  return null;
}

function FlyTo({
  latitude,
  longitude,
  zoom,
}: {
  latitude: number;
  longitude: number;
  zoom: number;
}) {
  const map = useMap();

  useEffect(() => {
    map.flyTo([latitude, longitude], zoom, { duration: 0.7 });
  }, [latitude, longitude, zoom, map]);

  return null;
}

function ClickCatcher({
  onInspect,
}: {
  onInspect: (lat: number, lng: number) => void;
}) {
  useMapEvents({
    click(event) {
      const target = event.originalEvent.target as HTMLElement | null;
      if (target?.closest?.(".leaflet-interactive")) {
        return;
      }
      onInspect(event.latlng.lat, event.latlng.lng);
    },
  });
  return null;
}

const FestMap: FC<FestMapProps> = ({
  fests,
  selectedId,
  onSelectFest,
  onInspect,
  flyTo,
  inspect,
}) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return <div style={{ width: "100%", height: "100%", background: "#c9d4c4" }} />;
  }

  return (
    <MapContainer
      center={[39.5, -96]}
      zoom={4}
      minZoom={3}
      maxZoom={12}
      scrollWheelZoom
      worldCopyJump={false}
      style={{ width: "100%", height: "100%", background: "#c9d4c4" }}
    >
      <SetupMap />
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
        url={cartoTiles("rastertiles/voyager")}
      />
      {fests.map((fest) => {
        const selectedFeature = fest.id === selectedId;
        return (
          <CircleMarker
            key={fest.id}
            center={[fest.latitude, fest.longitude]}
            radius={selectedFeature ? 11 : 8}
            pathOptions={pinPath(fest, selectedFeature)}
            eventHandlers={{
              click: () => onSelectFest(fest),
            }}
          >
            <Tooltip direction="top" offset={[0, -4]}>
              <span className="font-semibold">{fest.shortName}</span>
              <span> · {fest.city}, {fest.state}</span>
            </Tooltip>
          </CircleMarker>
        );
      })}
      {inspect && (
        <CircleMarker
          center={[inspect.latitude, inspect.longitude]}
          radius={6}
          pathOptions={{
            color: "#E2991A",
            fillColor: "#F6BB51",
            fillOpacity: 0.9,
            weight: 2,
          }}
        />
      )}
      {flyTo && (
        <FlyTo
          latitude={flyTo.latitude}
          longitude={flyTo.longitude}
          zoom={flyTo.zoom ?? 8}
        />
      )}
      <ClickCatcher onInspect={onInspect} />
    </MapContainer>
  );
};

export default FestMap;
